import { DialogLayout, InlineError, Stack } from '@/ui';
import { Button, type DialogProps } from '@/ui/primitives';
import type { SpacingToken } from '@/theme/tokens';
import type { KeyboardEvent, ReactNode } from 'react';

type AddDialogProps = {
  open: boolean;
  title: string;
  titleIcon?: ReactNode;
  saving?: boolean;
  disableSave?: boolean;
  saveLabel?: string;
  description?: ReactNode;
  errorMessage?: string | null;
  contentSx?: DialogProps['sx'];
  dialogSx?: DialogProps['sx'];
  dialogRadius?: string;
  contentGap?: SpacingToken;
  childrenGap?: SpacingToken;
  footerGap?: SpacingToken;
  onClose: () => void;
  onDialogClose?: DialogProps['onClose'];
  onSave: () => void | Promise<void>;
  children: ReactNode;
  maxWidth?: DialogProps['maxWidth'];
};

export const AddDialog = ({
  open,
  title,
  titleIcon,
  saving = false,
  disableSave = false,
  saveLabel = 'Save',
  description,
  errorMessage,
  contentSx,
  dialogSx,
  dialogRadius,
  contentGap,
  childrenGap = 'md',
  footerGap,
  onClose,
  onDialogClose,
  onSave,
  children,
  maxWidth = 'sm',
}: AddDialogProps): ReactNode => {
  const handleContentKeyDownCapture = (event: KeyboardEvent<HTMLDivElement>): void => {
    if (saving || disableSave) return;
    if (event.defaultPrevented || event.key !== 'Enter') return;
    const target = event.target as HTMLElement | null;
    const tagName = target?.tagName;
    if (tagName === 'TEXTAREA' || target?.isContentEditable) return;
    event.preventDefault();
    void onSave();
  };
  const resolvedDialogSx: DialogProps['sx'] = dialogRadius
    ? ([
        dialogSx,
        {
          '& .MuiDialog-paper': {
            borderRadius: `${dialogRadius} !important`,
          },
        },
      ].filter(Boolean) as DialogProps['sx'])
    : dialogSx;

  return (
    <DialogLayout
      open={open}
      onClose={onDialogClose ?? onClose}
      maxWidth={maxWidth}
      title={title}
      titleIcon={titleIcon}
      description={description}
      dialogSx={resolvedDialogSx}
      contentSx={contentSx}
      contentGap={contentGap}
      footerGap={footerGap}
      onContentKeyDownCapture={handleContentKeyDownCapture}
      actions={
        <>
          <Button
            variant="secondary"
            onClick={onClose}
            disabled={saving}
            aria-label="Cancel dialog"
          >
            Cancel
          </Button>
          <Button
            variant="primary"
            loading={saving}
            onClick={() => {
              void onSave();
            }}
            disabled={saving || disableSave}
            aria-label="Save dialog"
          >
            {saveLabel}
          </Button>
        </>
      }
    >
      <Stack gap={childrenGap}>
        {children}
        {errorMessage ? <InlineError message={errorMessage} /> : null}
      </Stack>
    </DialogLayout>
  );
};
